import { Trans, useTranslation } from "react-i18next";

import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";
import { CarImageSlide } from "./CarImageSlider";
import { PreviewCar } from "./PreviewCar";
import { CarFeature } from "./CarFeature";
import { Specification } from "./Specification";
import { LazyImage } from "./LazyImage";
import { Video360 } from "./Video360";
import { View360Icon } from "../../../../components/icons";
import { Reveal } from "../../../../components/motion";

export const FleetTypeDetail = ({
  data,
  previewImage,
  setPreviewImage,
}) => {
  const { t, i18n } = useTranslation();
  const getTypographyClass = createTypographyHelper(i18n.language);
  const isJapanese = i18n.language === "jp";
  const vehicleTitle = t(`FleetTypePage.${data.id}.Title`);
  const hasVideo = Boolean(data.video);
  const hasSeatMap = Boolean(data.seatImage);
  // Older tabs have always shown this shared sentence. New-layout tabs use their own copy.
  const videoIntroText = data.usesNewLayout
    ? t(`FleetTypePage.${data.id}.360View.Description`)
    : t("Toyota.Alphard.Video.Intro.Additional");

  return (
    <>
      <div className="tw:px-5 tw:lg:px-0 tw:mt-7 tw:lg:mt-[54px]">
        {data.usesNewLayout ? (
          // Keyed by vehicle so the card animates in again when the tab changes.
          <Reveal key={data.id}>
            <div className="tw:flex tw:flex-col tw:xl:flex-row tw:items-center tw:justify-center tw:gap-8 tw:xl:gap-12 tw:rounded-[10px] tw:bg-[#F9F9F9] tw:px-[25px] tw:py-7 tw:lg:px-[68px] tw:lg:py-8">
              <LazyImage
                src={data.carImage}
                alt={vehicleTitle}
                wrapperClassName="tw:w-full tw:max-w-[300px] tw:lg:max-w-[451px] tw:shrink-0"
                className="tw:w-full tw:h-[150px] tw:lg:h-[240px] tw:object-contain"
              />
              <div className="tw:w-full tw:xl:w-auto tw:xl:max-w-[550px]">
                <div className="tw:font-semibold tw:text-[#282828] tw:tracking-[3px] tw:text-[8px] tw:leading-[12px] tw:font-hanken-grotesk tw:uppercase">
                  {t(`FleetTypePage.${data.id}.Tag`)}
                </div>
                <div
                  className={cn(
                    "tw:mt-[10px] tw:lg:mt-3 tw:text-[#282828] tw:font-libre-caslon-display tw:text-[26px] tw:leading-[35px]",
                    isJapanese
                      ? "tw:lg:text-[40px] tw:lg:leading-[60px]"
                      : "tw:lg:text-[45px] tw:lg:leading-[58px]"
                  )}
                >
                  {vehicleTitle}
                </div>
                <div
                  className={cn(
                    "tw:mt-4 tw:lg:mt-6 tw:pt-4 tw:lg:pt-6 tw:border-t tw:border-t-[#F1F1F1] tw:text-[#4E4E4E] tw:font-hanken-grotesk",
                    getTypographyClass(12),
                    getTypographyClass(13, "lg")
                  )}
                >
                  {t(`FleetTypePage.${data.id}.Description`)}
                </div>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="tw:xl:px-[140px] tw:flex tw:flex-col tw:gap-y-8 tw:2xl:flex-row tw:items-center tw:rounded-[10px] tw:overflow-hidden tw:bg-[#F9F9F9] tw:2xl:h-[360px]">
            <div className="tw:px-4 tw:lg:px-0 tw:pt-4 tw:lg:pt-0 tw:w-full tw:2xl:w-auto">
              <LazyImage
                src={data.carImage}
                alt="Airport Transfer Card"
                wrapperClassName="tw:w-full tw:lg:w-[417px] tw:lg:min-w-[417px]"
                className="tw:w-full tw:lg:w-[417px] tw:lg:min-w-[417px] tw:rounded-t-[10px] tw:lg:rounded-none tw:flex-1"
              />
            </div>
            <div>
              <div className="tw:px-[25px] tw:lg:px-[48px] tw:lg:pt-[56px] tw:pb-7 tw:lg:pb-[56px] tw:space-y-[10px]">
                <div className="tw:pb-4 tw:border-b tw:border-b-[#F1F1F1] tw:mb-4">
                  <div className="tw:font-semibold tw:text-[#282828] tw:tracking-[3px] tw:text-[8px] tw:font-hanken-grotesk tw:uppercase">
                    {t(`FleetTypePage.${data.id}.Tag`)}
                  </div>
                  <div className="tw:text-[#282828] tw:font-libre-caslon-display tw:text-[26px] tw:lg:text-[45px] tw:leading-[35px] tw:lg:leading-[58px]">
                    {vehicleTitle}
                  </div>
                </div>
                <div
                  className={cn(
                    "tw:text-[#4E4E4E] tw:font-hanken-grotesk",
                    getTypographyClass(12),
                    getTypographyClass(13, "lg")
                  )}
                >
                  {t(`FleetTypePage.${data.id}.Description`)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Features & Amenities */}
      <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:border-b tw:border-b-[#F1F1F1] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row">
        <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px]">
          {t("FeaturesAmenities")}
        </div>
        <div
          className={cn(
            "tw:mt-5 tw:lg:mt-0 tw:grid tw:grid-cols-12 tw:border tw:border-[#F1F1F1] tw:rounded-md tw:w-full",
            getTypographyClass(13),
            getTypographyClass(14, "lg")
          )}
        >
          <CarFeature passengers={data.passenger} suitcases={data.suitcase} />
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row">
        <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px]">
          {t("Vehicle.Details")}
        </div>
        <div
          className={cn(
            "tw:mt-5 tw:lg:mt-0",
            getTypographyClass(13),
            getTypographyClass(14, "lg")
          )}
        >
          <Trans
            i18nKey={`FleetTypePage.${data.id}.VehicleDetails.Description`}
            components={{
              strong: <strong></strong>,
            }}
          />
        </div>
      </div>
      <div className="tw:relative tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-[74px] tw:lg:pb-[104px] tw:border-b tw:border-b-[#F1F1F1] tw:text-[#282828]">
        <CarImageSlide images={data.images} mobileSlidesToShow={data.mobileSlidesToShow} />
      </div>

      {/* 360-degree View */}
      <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row tw:border-b tw:border-b-[#F1F1F1]">
        <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px] tw:flex tw:lg:flex-col tw:gap-x-6">
          <div>
            <View360Icon aria-label="360-degree" role="img" />
          </div>
          <div className="tw:font-semibold tw:leading-[25px] tw:text-lg tw:mt-4">
            {t("360-degree-view")}
          </div>
        </div>
        <div
          className={cn(
            "tw:mt-5 tw:lg:mt-0 tw:w-full",
            getTypographyClass(13),
            getTypographyClass(14, "lg")
          )}
        >
          <div className="tw:whitespace-pre-line">{videoIntroText}</div>
          <div className="tw:mt-6">
            {hasVideo ? (
              <Video360
                src={data.video}
                title={`${vehicleTitle} VR 360 Video by THE LUXURY FLEET Co., Ltd.`}
              />
            ) : (
              <Reveal key={data.id}>
                <LazyImage
                  src={data.videoPreviewImage}
                  alt={`${vehicleTitle} interior`}
                  wrapperClassName="tw:w-full tw:aspect-video"
                  className="tw:w-full tw:h-full tw:object-cover tw:rounded-[5px]"
                />
              </Reveal>
            )}
          </div>
        </div>
      </div>
      <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row">
        <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px]">
          <div className="tw:font-semibold tw:leading-[25px] tw:text-lg tw:mt-4">
            {t("Specifications")}
          </div>
        </div>
        <div
          className={cn(
            "tw:mt-5 tw:lg:mt-0 tw:w-full tw:flex tw:flex-col tw:xl:flex-row tw:pb-8 tw:gap-y-8",
            getTypographyClass(13),
            getTypographyClass(14, "lg")
          )}
        >
          {hasSeatMap && (
            <div className="tw:mx-auto">
              <LazyImage
                src={data.seatImage}
                className="tw:max-w-full tw:h-auto"
                alt={`${vehicleTitle} seat map`}
              />
            </div>
          )}
          <div className="tw:flex-1">
            <div className="tw:text-[#282828] tw:text-[8px] tw:tracking-[3px] tw:font-semibold tw:uppercase">
              {t(`FleetTypePage.${data.id}.Tag`)}
            </div>
            <div
              className={cn(
                "tw:text-[#282828] tw:font-libre-caslon-display tw:mt-3",
                !data.usesNewLayout && "tw:text-[34px] tw:leading-[45px]",
                data.usesNewLayout && "tw:text-[26px] tw:leading-[35px] tw:lg:leading-[42px]",
                data.usesNewLayout && (isJapanese ? "tw:lg:text-[28px]" : "tw:lg:text-[32px]")
              )}
            >
              {vehicleTitle}
            </div>
            <Specification {...data} />
          </div>
        </div>
      </div>
      <PreviewCar
        previewImage={previewImage}
        setPreviewImage={setPreviewImage}
        images={data.images.map((img) => img.src)}
      />
    </>
  );
};
